import { getPeopleRecommendations } from "@mirage/service-typeahead-search";
import { LOCAL_FILE_CONNECTOR } from "@mirage/shared/connectors";
import { filterableContentTypes } from "@mirage/shared/content-type/content-types";
import { getLastUpdatedObjects } from "@mirage/shared/last-updated/last-updated";
import {
  buildAllFiltersById,
  mergeFilterTypes,
  SearchFilterType,
} from "@mirage/shared/search/search-filters";
import { atom, useSetAtom } from "jotai";

import { DatePickerRangeSelection } from "@dropbox/dig-components/date_picker";
import { useLastUpdatedCustomFilter } from "@mirage/mosaics/LastUpdatedFilter/useLastUpdatedCustomFilter";
import {
  connectionsAtom,
  connectorsAtom,
} from "@mirage/service-connectors/useConnectors";
import { tagged } from "@mirage/service-logging";
import { connectionToSearchFilter } from "@mirage/service-typeahead-search/service/sources/search-filters";
import type { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { useEffect } from "react";

const logger = tagged("omnibox-atoms-filters");

export function useInitializeFilterAtoms({
  isLocalFilesEnabled,
  isTypeaheadFiltersEnabled,
}: {
  isLocalFilesEnabled: boolean;
  isTypeaheadFiltersEnabled: boolean;
}) {
  const { customDateRange } = useLastUpdatedCustomFilter();

  const setIsLocalFilesEnabled = useSetAtom(IsLocalFilesEnabledAtom);
  const setCustomDateRange = useSetAtom(CustomDateRangeAtom);
  const setIsTypeaheadFiltersEnabled = useSetAtom(
    IsTypeaheadFiltersEnabledAtom,
  );

  useEffect(() => {
    setIsLocalFilesEnabled(isLocalFilesEnabled);
  }, [isLocalFilesEnabled, setIsLocalFilesEnabled]);

  useEffect(() => {
    setCustomDateRange(customDateRange);
  }, [customDateRange, setCustomDateRange]);

  useEffect(() => {
    setIsTypeaheadFiltersEnabled(isTypeaheadFiltersEnabled);
  }, [isTypeaheadFiltersEnabled, setIsTypeaheadFiltersEnabled]);
}

export const IsLocalFilesEnabledAtom = atom<boolean>(false);
export const CustomDateRangeAtom = atom<DatePickerRangeSelection | undefined>(
  undefined,
);
export const IsTypeaheadFiltersEnabledAtom = atom<boolean>(false);
export const PeopleRecommendationsAtom = atom<Promise<PeopleRecommendation[]>>(
  async () => {
    try {
      return await getPeopleRecommendations();
    } catch (error) {
      logger.error("Failed to fetch people recommendations:", error);
      return [];
    }
  },
);

export const AllFiltersAtom = atom<SearchFilter[]>((get) => {
  const connectors = get(connectorsAtom);
  const isLocalFilesEnabled = get(IsLocalFilesEnabledAtom);
  const peopleRecommendations = get(PeopleRecommendationsAtom);
  const customDateRange = get(CustomDateRangeAtom);

  const STATIC_CONNECTORS = [];

  if (isLocalFilesEnabled) {
    STATIC_CONNECTORS.push(LOCAL_FILE_CONNECTOR);
  }

  return mergeFilterTypes(
    [...connectors, ...STATIC_CONNECTORS],
    filterableContentTypes,
    getLastUpdatedObjects(customDateRange),
    peopleRecommendations instanceof Promise ? [] : peopleRecommendations,
  );
});

export const AllFiltersByIdAtom = atom<Record<string, SearchFilter>>((get) => {
  const allFilters = get(AllFiltersAtom);
  return buildAllFiltersById(allFilters);
});

export const FiltersAtom = atom<SearchFilter[]>([]);

export const FiltersWithParametersAtom = atom<SearchFilter[]>((get) => {
  const filters = get(FiltersAtom);

  const connections = get(connectionsAtom);

  if (!connections?.length) return filters;

  return (filters || [])
    .map((filter) => {
      if (filter.type !== SearchFilterType.Connector) return filter;

      const connector = connections.find(
        (c) => c.connector?.id_attrs?.type === filter.id,
      );

      if (!connector) return;

      return connectionToSearchFilter(connector);
    })
    .filter(isDefined);
});
